import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

export function clone(obj: any): any {
  return JSON.parse(JSON.stringify(obj));
}

export function getValueOrUnknown(value: any) {
  if (value === null || value === "" || value == undefined) {
    return "Unknown";
  }

  return value;
}

export function getOS() {
  const userAgent = navigator.userAgent;
  if (/windows/i.test(userAgent)) {
    return "Windows";
  }

  if (/mac/i.test(userAgent)) {
    return "MacOS";
  }

  return "Unknown";
}

export function isMacOS() {
  return getOS() === "MacOS";
}

export function isCtrlOrCmdPressed(event: KeyboardEvent) {
  return isMacOS() ? event.metaKey : event.ctrlKey;
}

export function deleteEmptyArrayInObject(data: { [key: string]: [] }) {
  Object.keys(data).forEach((key) => {
    if (Array.isArray(data[key]) && data[key].length === 0) {
      delete data[key];
    }
  });

  return data;
}

export function handleDecimal(value: any, decimalNumber: number = 0) {
  if (value === null || value === "" || value === undefined) {
    return "-";
  }
  return decimalNumber
    ? removeTrailingZeros(value.toFixed(decimalNumber))
    : value;
}

export function groupBy(list: any[], property: string) {
  return list?.reduce((groups, item) => {
    const val = item[property];
    groups[val] = groups[val] || [];
    groups[val].push(item);
    return groups;
  }, {});
}

export function sortObject(obj: any) {
  // Get an array of the keys:
  let keys = Object.keys(obj);

  // Then sort by using the keys to lookup the values in the original object:
  keys.sort((a, b) => obj[b] - obj[a]);

  return keys;
}

export function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export function getRandomColor() {
  return "#" + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, "0");
}

export function validateEmail(email: string) {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
}

// to get time format in HHMMA 0506PM
export function getTimeFormat(time: any) {
  const am_pm = time.split(" ")[1];
  let hours = time.split(" ")[0]?.split(":")[0];
  const mins = time.split(" ")[0]?.split(":")[1];
  hours = hours?.length > 1 ? hours : "0" + hours;
  return hours + mins + am_pm;
}

// Custom validator to enforce a pattern using regular expressions
export function patternValidator(pattern: RegExp): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const valid = pattern.test(control.value);
    return valid ? null : { pattern: { value: control.value } };
  };
}

export function isStateCompleted(stateName: string) {
  const allStates = JSON.parse(localStorage.getItem("sideBarState")!);
  let value = false;
  allStates.forEach((each: any) => {
    if (each.title == stateName) {
      value = each.status == "COMPLETED" ? true : false;
    }
  });
  return value;
}

export function isStatePending(stateName: string) {
  const allStates = JSON.parse(localStorage.getItem("sideBarState")!);
  let value: any = "";
  allStates.forEach((each: any) => {
    if (each.title == stateName) {
      value = each.status == "PENDING" ? "pending" : "sucess";
    }
  });
  return value;
}

export function formatDateToYYYYMMDD(date: Date): string {
  const year = date?.getFullYear();
  const month = (date?.getMonth() + 1).toString().padStart(2, "0");
  const day = date?.getDate().toString().padStart(2, "0");
  return `${year}${month}${day}`;
}

export function fixedFloat(value: any) {
  const num = parseFloat(value); // Convert to number if it's a string
  return isNaN(num) ? 0 : parseFloat(num.toFixed(2)); // Ensure valid number, otherwise return 0
}

export function removeTrailingZeros(number: any) {
  let num = Number(number);
  return num % 1 === 0 ? num : parseFloat(num.toString());
}

// To handle empty content in the form fields
export function noWhitespaceValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const isWhitespace = (control.value || "").trim().length === 0;
    return isWhitespace ? { required: true } : null;
  };
}

// Deep merge two objects only if the key is not in the skipKeys set
export function deepAssign(
  target: any,
  source: any,
  skipKeys: Set<string> = new Set(),
  parentKey: string = ""
): any {
  if (isObject(source)) {
    Object.keys(source).forEach((key) => {
      let fullKey = parentKey ? `${parentKey}.${key}` : key;
      if (skipKeys.has(fullKey)) {
        return;
      }
      if (isObject(source[key])) {
        if (!isObject(target[key])) {
          target[key] = {};
        }
        // Recursively merge nested objects
        deepAssign(target[key], source[key], skipKeys, fullKey);
      } else {
        target[key] = source[key]; // Direct assignment for primitive values
      }
    });
  }
  return target;
}

export function constructDotNotationKeys(
  object: { [key: string]: any },
  parent: string = "",
  result: Set<string> = new Set()
) {
  for (let key in object) {
    if (object.hasOwnProperty(key)) {
      const newKey = parent ? `${parent}.${key}` : key;

      if (isObject(object[key])) {
        // Recursively traverse nested objects
        constructDotNotationKeys(object[key], newKey, result);
      } else {
        // Add key in dot notation to the result array
        result.add(newKey);
      }
    }
  }
  return result;
}

// Helper function to check if value is an object but not an array
export function isObject(value: any) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// Helper function to compare and generate undo/redo entries
export function compareKeys(
  current: any,
  original: any,
  undo: { [key: string]: any },
  redo: { [key: string]: any },
  skipKeys: Set<string> = new Set()
) {
  // Iterate through keys of the current object
  for (const key in current) {
    if (current.hasOwnProperty(key) && !skipKeys.has(key)) {
      if (!original.hasOwnProperty(key)) {
        // Key is newly added in current
        undo[key] = null; // Set undo to null (key didn't exist earlier)
        redo[key] = current[key]; // Set redo to the new value
      } else if (isObject(current[key])) {
        // Recursively handle nested objects (if the value is another object but not an array)
        undo[key] = {};
        redo[key] = {};
        compareKeys(
          current[key],
          original[key] || {},
          undo[key],
          redo[key],
          skipKeys
        );
        Object.keys(undo[key]).length === 0 && delete undo[key];
        Object.keys(redo[key]).length === 0 && delete redo[key];
      } else if (current[key] !== original[key]) {
        // Key exists in both but has changed
        undo[key] = original[key]; // Set undo to the original value
        redo[key] = current[key]; // Set redo to the current value
      }
    }
  }

  // Iterate through keys of the original object
  for (const key in original) {
    if (
      original.hasOwnProperty(key) &&
      !current.hasOwnProperty(key) &&
      !skipKeys.has(key)
    ) {
      // Key was removed from the current object
      undo[key] = original[key]; // Set undo to the original value (re-add the key)
      redo[key] = null; // Set redo to null (key was removed)
    }
  }
}

export function getRandomNumber(min: number, max: number) {
  return parseFloat((Math.random() * (max - min) + min).toFixed(2));
}

export function setDecimalValue(value: number, decimalPoint: number = 0) {
  if (typeof value === "string") {
    return parseFloat(value).toFixed(decimalPoint);
  } else {
    return value ? value.toFixed(decimalPoint) : parseFloat("0");
  }
}

export function wrapText(context: any, text: any, maxWidth: any) {
  const words = text?.split(" ") || [];

  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const testLine = `${currentLine} ${word}`;
    const testWidth = context.measureText(testLine).width;

    if (testWidth > maxWidth) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  lines.push(currentLine); // Add the last line
  return lines;
}
